import { parse, toSeconds } from 'iso8601-duration';
import { z } from 'zod';

import { InsuranceType } from '@/utils/constants.js';

/**
 * Runtime-validated model for the public chain-configuration list endpoint. Field names stay
 * `snake_case` (wire DTO forwarded verbatim downstream). Mirrors the availability-api schema.
 * Validates field shapes + the per-field minimums and drops-invalid-with-warn; it does NOT
 * re-assert the server's cross-field invariants (e.g. gaps.length === steps.length - 1).
 */

const MAX_GAP_DURATION_IN_DAYS = 5000;
export const DAY_HOUR_MINUTE_DURATION_REGEX = /^P(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?)?$/;
const MAX_GAP_DURATION_IN_SECONDS = MAX_GAP_DURATION_IN_DAYS * 24 * 60 * 60;

export const durationToSeconds = (val: string): number | null => {
    try {
        return toSeconds(parse(val));
    } catch {
        return null;
    }
};

export const durationSchema = z.iso
    .duration()
    .refine((val) => DAY_HOUR_MINUTE_DURATION_REGEX.test(val), {
        message: 'Duration must use only days, hours and minutes (e.g., P7D, PT90M, P1DT2H30M)',
    })
    .superRefine((val, ctx) => {
        const seconds = durationToSeconds(val);
        if (seconds === null) {
            ctx.addIssue({
                code: 'custom',
                message: 'Duration must be a valid ISO 8601 duration (e.g., P7D, PT90M)',
            });
            return;
        }
        if (seconds > MAX_GAP_DURATION_IN_SECONDS) {
            ctx.addIssue({
                code: 'custom',
                message: `Duration must not exceed ${MAX_GAP_DURATION_IN_DAYS} days`,
            });
        }
    });

export const stepEligiblePractitionerSchema = z.object({
    practitioner_id: z.string(),
    practitioner_pms_id: z.string(),
    calendar_pms_ids: z.array(z.string()),
    prismic_key: z.string(),
    display_name: z.string(),
});

/** A chain step as returned by the public list endpoint. */
export const chainStepSchema = z.object({
    order: z.int().nonnegative(),
    appointment_type_id: z.string(),
    is_main_practitioner: z.boolean(),
    eligible_practitioners: z.array(stepEligiblePractitionerSchema),
});

export const chainGapSchema = z
    .object({
        from_step: z.number().int().nonnegative(),
        to_step: z.number().int().nonnegative(),
        min_duration: durationSchema,
        max_duration: durationSchema.optional(),
    })
    .refine(
        (data) => {
            if (!data.max_duration) {
                return true;
            }
            const minSeconds = durationToSeconds(data.min_duration);
            const maxSeconds = durationToSeconds(data.max_duration);
            if (minSeconds === null || maxSeconds === null) {
                return true;
            }
            return minSeconds <= maxSeconds;
        },
        { message: 'min_duration must be less than or equal to max_duration' }
    );

export const appointmentChainConfigSchema = z
    .object({
        id: z.uuid(),
        instance_id: z.uuid(),
        customer_id: z.uuid(),
        name: z.object({
            en: z.string(),
            de: z.string(),
        }),
        description: z
            .object({
                de: z.string(),
            })
            .optional(),
        practice_id: z.string(),
        insurance_types: z.array(z.enum(InsuranceType)).min(1),
        is_for_new_patients: z.boolean(),
        is_for_returning_patients: z.boolean(),
        is_bookable_by_patient: z.boolean().default(false),
        steps: z.array(chainStepSchema).min(2),
        gaps: z.array(chainGapSchema).min(1),
        meta: z.object({
            created_at: z.iso.datetime(),
            updated_at: z.iso.datetime(),
            deleted_at: z.string().optional(),
        }),
    })
    .refine((config) => config.is_for_new_patients || config.is_for_returning_patients, {
        message: 'At least one patient type must be selected',
        path: ['is_for_new_patients'],
    });

export type StepEligiblePractitioner = z.infer<typeof stepEligiblePractitionerSchema>;
export type ChainStep = z.infer<typeof chainStepSchema>;
export type ChainGap = z.infer<typeof chainGapSchema>;
export type AppointmentChainConfig = z.infer<typeof appointmentChainConfigSchema>;

/**
 * Validates the response body's `data` array: non-array throws; each item is `safeParse`d with
 * invalid items dropped + warned; all-invalid (with >=1 item) throws; `200 + []` returns `[]`.
 */
export const parseChainConfigs = (raw: unknown): AppointmentChainConfig[] => {
    if (!Array.isArray(raw)) {
        throw new TypeError(`chain-configs: expected array, got ${raw === null ? 'null' : typeof raw}`);
    }

    const valid = raw.flatMap((item, index) => {
        const parsed = appointmentChainConfigSchema.safeParse(item);
        if (parsed.success) return [parsed.data];
        console.warn(`chain-configs: dropped invalid config at index ${index}:`, parsed.error.issues);
        return [];
    });

    if (raw.length > 0 && valid.length === 0) {
        throw new Error('chain-configs: all configs failed validation');
    }

    return valid;
};
